import { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { useToast } from './use-toast';
import { app } from '../firebase';
import axios from 'axios';
import { saveAs } from 'file-saver';
import { useLoading } from './useLoading';

export const useCourseData = (courseId: string) => {
  const { toast } = useToast();
  const [courseData, setCourseData] = useState<any>(null);
  const [modules, setModules] = useState<any[]>([]);
  const [subjects, setSubjects] = useState<any[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<any>(null);
  const [batches, setBatches] = useState<any[]>([]);

  const db = getFirestore(app);
  const storage = getStorage(app);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const { setIsLoading } = useLoading();
  const [isLoading, setISLoading] = useState(false);

  // Fetch course data and subjects
  useEffect(() => {
    const fetchCourseData = async () => {
      setIsLoading(true);
      try {
        // Reference to the specific course document in Firestore
        const courseRef = doc(db, 'courses', courseId);
        const courseSnap = await getDoc(courseRef);

        if (courseSnap.exists()) {
          const fetchedCourse: any = { id: courseSnap.id, ...courseSnap.data() };
          setCourseData(fetchedCourse);

          // Fetch subjects for this course
          if (fetchedCourse.subjects && fetchedCourse.subjects.length > 0) {
            // Get full subject data
            const subjectPromises = fetchedCourse.subjects.map(async (subjectRef: any) => {
              try {
                const subjectDoc = await getDoc(doc(db, 'subjects', subjectRef.subjectId));
                if (subjectDoc.exists()) {
                  return { id: subjectDoc.id, ...subjectDoc.data(), ...subjectRef };
                }
                return null;
              } catch (error) {
                console.error("Error fetching subject:", error);
                return null;
              }
            });

            const fetchedSubjects = (await Promise.all(subjectPromises)).filter(subject => subject !== null);
            setSubjects(fetchedCourse?.subjects);

            // Select first subject by default
            if (fetchedCourse?.subjects.length > 0) {
              setSelectedSubject(fetchedCourse?.subjects[0]);
              await fetchModulesForSubject(fetchedCourse?.subjects[0]);
            }
          }
        } else {
          throw new Error('Course not found!');
        }
      } catch (error: any) {
        toast({
          title: "Error fetching course data",
          description: error.message || "There was an error loading the course details.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    const fetchAllBatches = async () => {
      try {
        // Reference to the batches collection
        const batchesCollection = collection(db, "courseBatches");
        const batchSnapShots = await getDocs(batchesCollection);

        // Map through the documents in the collection to extract data
        const fetchedBatches = batchSnapShots?.docs?.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setBatches(fetchedBatches);
      } catch (error: any) {
        toast({
          title: "Error fetching batches",
          description: error.message || "There was an error loading the batches.",
          variant: "destructive",
        });
      }
    };

    fetchCourseData();
    fetchAllBatches();
  }, [courseId, db, toast]);

  // Function to fetch modules for a selected subject
  const fetchModulesForSubject = async (subject: any) => {
    if (!subject || !subject.moduleIds || subject.moduleIds.length === 0) {
      setModules([]);
      return;
    }

    try {
      let modulesData: any[] = [];

      // Fetch modules in batches to avoid exceeding the "in" query limit
      const fetchLimit = 10; // Firestore has a limit of 10 for "in" queries

      for (let i = 0; i < subject.moduleIds.length; i += fetchLimit) {
        const batch = subject.moduleIds.slice(i, i + fetchLimit);

        const modulesQuery = query(
          collection(db, 'modules'),
          where('id', 'in', batch)
        );

        const modulesSnapshot = await getDocs(modulesQuery);
        const batchModules = modulesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          completed: false // Default value
        }));

        modulesData = [...modulesData, ...batchModules];
      }

      // Sort modules based on their order in moduleIds array
      const sortedModules = [...modulesData].sort((a, b) => {
        return subject.moduleIds.indexOf(a.id) - subject.moduleIds.indexOf(b.id);
      });

      setModules(sortedModules);
    } catch (error: any) {
      console.error("Error fetching modules:", error);
      toast({
        title: "Error loading modules",
        description: error.message || "There was an error loading the modules for this subject.",
        variant: "destructive",
      });
      setModules([]);
    }
  };

  // Handle subject selection
  const handleSubjectClick = async (subject: any) => {
    setSelectedSubject(subject);
    await fetchModulesForSubject(subject);
  };

  // Handle file download
  const handleDownload1 = async (fileUrl: string) => {
    if (!fileUrl || fileUrl === "") {
      toast({
        title: "Error fetching course data",
        description: "File Not Exists",
        variant: "destructive",
      });
      return;
    }

    try {
      let response;

      if (fileUrl.includes("assets")) {
        // Logic for assets  
        // Replace 'your-assets-endpoint' with the actual URL or logic to fetch from Axioud  
        const assetUrl = `${fileUrl}`;  // ex :'../../assets/pdfs/CSharp - Module 1.pdf'' 
        response = await fetch(assetUrl);
      } else {
        // Normal fetch for non-assets URLs  
        response = await fetch(fileUrl);
      }

      if (!response.ok) {
        toast({
          title: "Network response was not ok",
          description: "File Not Exists",
          variant: "destructive",
        });
        throw new Error("Network response was not ok");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link: any = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileUrl.split("/").pop()); // Provide a name for the file  
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      toast({
        title: "Error downloading file",
        description: error.message || "File Not Exists",
        variant: "destructive",
      });
    }
  };



  const handleDownload = async (link) => {
    setLoading(true);
    setProgress(0);
    setIsLoading(true);
    try {
      const response = await axios.get(link, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (event.total) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
            setProgress(percentDone);
          }
        }
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      saveAs(file, link.split('/').pop()); // Save the file with the last part of the URL  
      setProgress(100); // Set progress to 100% on success  
      setIsLoading(false);
    } catch (error) {
      console.error('Download error:', error);
      setProgress(0); // Reset progress on error  
    } finally {
      setLoading(false);
    }
  };


  const handleSyllabusDownload = async () => {
    setLoading(true);
    setProgress(0);
    if (!courseData || !courseData?.syllabusUrl) {
      toast({
        title: "Download Error",
        description: "Syllabus file is not available.",
        variant: "destructive",
      });
      return;
    }
    const link = courseData?.syllabusUrl
    try {
      const response = await axios.get(link, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (event.total) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
            setProgress(percentDone);
          }
        }
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      saveAs(file, link.split('/').pop()); // Save the file with the last part of the URL  
      setProgress(100); // Set progress to 100% on success  
    } catch (error) {
      console.error('Download error:', error);
      setProgress(0); // Reset progress on error  
    } finally {
      setLoading(false);
    }
  };

  // Handle course syllabus download
  const handleSyllabusDownload1 = async () => {
    if (!courseData?.syllabusMaterial?.url || courseData?.syllabusMaterial?.url =="") {
      toast({
        title: "Error fetching course data",
        description: "File Not Exists",
        variant: "destructive",
      });
      return;
    }
    const fileUrl = courseData?.syllabusMaterial?.url;
    setIsLoading(true);
    setProgress(0);

    try {
      const response = await axios.get(fileUrl, {
        responseType: 'blob',
        onDownloadProgress: (event) => {
          if (event.total) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
            setProgress(percentDone);
          }
        }
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      saveAs(file, fileUrl.includes('assets') ? fileUrl.split('/').pop() : courseData?.syllabusMaterial?.fileName); // Save the file with the last part of the URL  
      setProgress(100); // Set progress to 100% on success  
      setIsLoading(false);
    } catch (error) {
      console.error('Download error:', error);
      setProgress(0); // Reset progress on error  
    } finally {
      setIsLoading(false);
    }
  };

  // Handle all materials download
  const handleAllMaterialsDownload = () => {
    // This would need to be implemented based on specific requirements
    toast({
      title: "Preparing Download",
      description: "Preparing all course materials for download...",
    });

    // Simulate download preparation delay
    setTimeout(() => {
      toast({
        title: "Download Started",
        description: "All course materials are being downloaded...",
      });

      // Simulate download completion delay
      setTimeout(() => {
        toast({
          title: "Download Complete",
          description: "All course materials have been downloaded successfully.",
        });
      }, 2500);
    }, 1000);
  };

  return {
    isLoading,
    courseData,
    modules,
    subjects,
    selectedSubject,
    batches,
    handleSubjectClick,
    fetchModulesForSubject,
    handleDownload,
    handleSyllabusDownload1,
    handleAllMaterialsDownload,
  };
};